import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Droplet, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './WaterIntakeCalculatorPage/components/CalculationForm';
import ResultsOverview from './WaterIntakeCalculatorPage/components/ResultsOverview';
import IntakeSchedule from './WaterIntakeCalculatorPage/components/IntakeSchedule';
import QuickAdjustmentSidebar from './WaterIntakeCalculatorPage/components/QuickAdjustmentSidebar';
import WaterTrackerSidebar from './WaterIntakeCalculatorPage/components/WaterTrackerSidebar';
import TipsSidebar from './WaterIntakeCalculatorPage/components/TipsSidebar';
import InfoContent from './WaterIntakeCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  weight: '70', // кг
  height: '170', // см
  age: '30', // лет
  gender: 'male', // 'male' или 'female'
  activityLevel: 'moderate', // 'sedentary', 'light', 'moderate', 'active', 'very_active'
  climate: 'temperate', // 'cold', 'temperate', 'hot'
  season: getCurrentSeason(), // 'winter', 'spring', 'summer', 'autumn'
  healthConditions: [], // массив состояний здоровья, влияющих на потребление воды
  pregnancyLactation: 'none', // 'none', 'pregnant', 'lactating'
  dietaryPreferences: 'standard', // 'standard', 'high_protein', 'vegetarian', 'vegan'
  caffeineConsumption: 'moderate', // 'none', 'low', 'moderate', 'high'
  alcoholConsumption: 'none', // 'none', 'low', 'moderate', 'high'
  waterIntakePreferences: {
    includesFoodWater: true, // учитывать ли воду из пищи
    includesOtherBeverages: true, // учитывать ли другие напитки
    preferredContainerSize: '250', // мл (стандартный стакан)
    wakeUpTime: '07:00', // время подъема
    bedTime: '23:00', // время отхода ко сну
  }
};

// Определение уровней активности
export const activityLevels = [
  { id: 'sedentary', name: 'Малоподвижный', description: 'Сидячая работа, минимум физической активности', factor: 1.2 },
  { id: 'light', name: 'Легкая активность', description: 'Легкие упражнения/прогулки 1-3 раза в неделю', factor: 1.375 },
  { id: 'moderate', name: 'Умеренная активность', description: 'Умеренные тренировки 3-5 раз в неделю', factor: 1.55 },
  { id: 'active', name: 'Активный', description: 'Интенсивные тренировки 6-7 раз в неделю', factor: 1.725 },
  { id: 'very_active', name: 'Очень активный', description: 'Ежедневные интенсивные тренировки или физический труд', factor: 1.9 }
];

// Определение климатических условий
export const climateTypes = [
  { id: 'cold', name: 'Холодный', description: 'Средняя температура ниже +10°C', factor: 0.9 },
  { id: 'temperate', name: 'Умеренный', description: 'Средняя температура от +10°C до +25°C', factor: 1.0 },
  { id: 'hot', name: 'Жаркий', description: 'Средняя температура выше +25°C', factor: 1.1 }
];

// Определение сезонов года
export const seasons = [
  { id: 'winter', name: 'Зима', factor: 0.95 },
  { id: 'spring', name: 'Весна', factor: 1.0 },
  { id: 'summer', name: 'Лето', factor: 1.05 },
  { id: 'autumn', name: 'Осень', factor: 1.0 }
];

// Определение состояний здоровья
export const healthConditionsList = [
  { id: 'kidney_disease', name: 'Заболевания почек', factor: 0.0, description: 'Требуется консультация врача' },
  { id: 'heart_disease', name: 'Сердечно-сосудистые заболевания', factor: 0.0, description: 'Требуется консультация врача' },
  { id: 'diabetes', name: 'Диабет', factor: 1.1, description: 'Обычно требуется больше воды' },
  { id: 'hypertension', name: 'Гипертония', factor: 0.0, description: 'Требуется консультация врача' },
  { id: 'urolithiasis', name: 'Мочекаменная болезнь', factor: 1.2, description: 'Рекомендуется увеличить потребление воды' },
  { id: 'edema', name: 'Отеки', factor: 0.0, description: 'Требуется консультация врача' },
  { id: 'fever', name: 'Повышенная температура', factor: 1.15, description: 'Требуется больше воды при лихорадке' },
  { id: 'diarrhea', name: 'Диарея/рвота', factor: 1.2, description: 'Требуется восполнение потери жидкости' }
];

// Получение текущего сезона
function getCurrentSeason() {
  const month = new Date().getMonth();
  if (month >= 11 || month <= 1) return 'winter';
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  return 'autumn';
}

/**
 * WaterIntakeCalculatorPage Component - Полный калькулятор нормы потребления воды с учетом различных параметров и факторов.
 */
const WaterIntakeCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('water-intake-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор суточной нормы воды | Gensio';
  const metaDescription = tool?.metaDescription || 'Точный расчет индивидуальной суточной нормы потребления воды с учетом веса, возраста, пола, уровня активности, климата, сезона и других факторов. Получите персональные рекомендации для оптимального водного баланса.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [waterConsumptionData, setWaterConsumptionData] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных профилей из localStorage ---
  useEffect(() => {
    const savedProfilesData = localStorage.getItem('savedWaterProfiles');
    if (savedProfilesData) {
      try {
        setSavedProfiles(JSON.parse(savedProfilesData));
      } catch (e) {
        console.error("Error loading saved profiles:", e);
      }
    }

    // Загрузка данных об употреблении воды за последнюю неделю
    const waterData = localStorage.getItem('waterConsumptionData');
    if (waterData) {
      try {
        setWaterConsumptionData(JSON.parse(waterData));
      } catch (e) {
        console.error("Error loading water consumption data:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'male':
          newState.gender = 'male';
          break;
        case 'female':
          newState.gender = 'female';
          break;
        case 'pregnancy':
          newState.gender = 'female';
          newState.pregnancyLactation = 'pregnant';
          break;
        case 'sport':
          newState.activityLevel = 'very_active';
          break;
        case 'office':
          newState.activityLevel = 'sedentary';
          break;
        case 'summer':
          newState.season = 'summer';
          newState.climate = 'hot';
          break;
        case 'winter':
          newState.season = 'winter';
          newState.climate = 'cold';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение профиля в localStorage
  const saveProfile = (name) => {
    if (!results) return;

    const newSavedProfile = {
      id: Date.now().toString(),
      name: name || `Профиль ${formData.weight}кг, ${formData.gender === 'male' ? 'М' : 'Ж'}, ${formData.age}л`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProfiles = [...savedProfiles, newSavedProfile];
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedWaterProfiles', JSON.stringify(updatedSavedProfiles));

    return newSavedProfile;
  };

  // Удаление сохраненного профиля
  const deleteSavedProfile = (id) => {
    const updatedSavedProfiles = savedProfiles.filter(profile => profile.id !== id);
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedWaterProfiles', JSON.stringify(updatedSavedProfiles));
  };

  // Загрузка сохраненного профиля
  const loadSavedProfile = (id) => {
    const profileToLoad = savedProfiles.find(profile => profile.id === id);
    if (profileToLoad) {
      setFormData(profileToLoad.formData);
      setResults(profileToLoad.results);
      // Генерируем график потребления воды заново
      if (profileToLoad.results) {
        generateSchedule(profileToLoad.results, profileToLoad.formData);
      }
    }
  };

  // Функция для генерации графика потребления воды
  const generateSchedule = (resultData, formSettings) => {
    if (!resultData || !formSettings) return;

    const totalWaterMl = resultData.recommendedWaterIntakeMl;
    const containerSizeMl = parseInt(formSettings.waterIntakePreferences.preferredContainerSize) || 250;
    const wakeUpTime = formSettings.waterIntakePreferences.wakeUpTime || "07:00";
    const bedTime = formSettings.waterIntakePreferences.bedTime || "23:00";

    const wakeUpHour = parseInt(wakeUpTime.split(":")[0]);
    const wakeUpMinute = parseInt(wakeUpTime.split(":")[1]) || 0;
    const bedHour = parseInt(bedTime.split(":")[0]);
    const bedMinute = parseInt(bedTime.split(":")[1]) || 0;

    let wakeUpMinutes = wakeUpHour * 60 + wakeUpMinute;
    let bedMinutes = bedHour * 60 + bedMinute;
    
    // Если время отхода ко сну раньше времени пробуждения (следующий день), добавляем 24 часа
    if (bedMinutes <= wakeUpMinutes) {
      bedMinutes += 24 * 60;
    }

    const totalAwakeMinutes = bedMinutes - wakeUpMinutes;
    const totalContainers = Math.ceil(totalWaterMl / containerSizeMl);

    // Распределяем контейнеры равномерно в течение дня
    const schedule = [];
    const minutesBetweenContainers = Math.floor(totalAwakeMinutes / (totalContainers + 1));

    // Добавляем первый прием воды сразу после пробуждения
    schedule.push({
      time: formatTime(wakeUpMinutes),
      amount: containerSizeMl,
      note: "Сразу после пробуждения",
      percentOfDaily: Math.round((containerSizeMl / totalWaterMl) * 100)
    });

    // Добавляем регулярные приемы воды в течение дня
    for (let i = 1; i < totalContainers - 1; i++) {
      const timeMinutes = wakeUpMinutes + minutesBetweenContainers * i;
      schedule.push({
        time: formatTime(timeMinutes),
        amount: containerSizeMl,
        note: getTimeOfDayNote(timeMinutes),
        percentOfDaily: Math.round((containerSizeMl / totalWaterMl) * 100)
      });
    }

    // Добавляем последний прием воды за 2 часа до сна
    const lastIntakeMinutes = bedMinutes - 120;
    schedule.push({
      time: formatTime(lastIntakeMinutes),
      amount: containerSizeMl,
      note: "За 2 часа до сна",
      percentOfDaily: Math.round((containerSizeMl / totalWaterMl) * 100)
    });

    setSchedule(schedule);
  };

  // Вспомогательная функция для форматирования времени
  const formatTime = (totalMinutes) => {
    totalMinutes = totalMinutes % (24 * 60); // Приводим к диапазону 0-1439 минут (24 часа)
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  // Вспомогательная функция для определения времени суток
  const getTimeOfDayNote = (totalMinutes) => {
    totalMinutes = totalMinutes % (24 * 60);
    const hour = Math.floor(totalMinutes / 60);

    if (hour >= 6 && hour < 12) return "Утро";
    if (hour >= 12 && hour < 18) return "День";
    if (hour >= 18 && hour < 22) return "Вечер";
    return "Ночь";
  };

  // Обновление данных о потреблении воды
  const updateWaterConsumptionData = (date, amount) => {
    const today = date || new Date().toISOString().split('T')[0];
    
    const updatedData = [...waterConsumptionData];
    const existingDataIndex = updatedData.findIndex(item => item.date === today);
    
    if (existingDataIndex >= 0) {
      updatedData[existingDataIndex].amount += amount;
    } else {
      updatedData.push({ date: today, amount });
    }
    
    // Сохраняем только данные за последние 30 дней
    const sortedData = updatedData
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 30);
    
    setWaterConsumptionData(sortedData);
    localStorage.setItem('waterConsumptionData', JSON.stringify(sortedData));
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.blue.100)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.blue.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Droplet className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор суточной нормы воды'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setSchedule={setSchedule}
              generateSchedule={generateSchedule}
              activityLevels={activityLevels}
              climateTypes={climateTypes}
              seasons={seasons}
              healthConditionsList={healthConditionsList}
              saveProfile={saveProfile}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <IntakeSchedule 
                  schedule={schedule} 
                  results={results}
                  updateWaterConsumptionData={updateWaterConsumptionData}
                />
              </>
            )}

            {/* --- Информационный контент о воде --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Трекер потребления воды --- */}
            <WaterTrackerSidebar 
              waterConsumptionData={waterConsumptionData}
              updateWaterConsumptionData={updateWaterConsumptionData}
              recommendedAmount={results?.recommendedWaterIntakeMl || 2000}
              formData={formData}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Water Droplet Animation */
        .water-drop {
          animation: drop 1.5s ease-in-out infinite;
        }
        @keyframes drop {
          0% { transform: translateY(-5px) scale(0.9); opacity: 0.7; }
          50% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-5px) scale(0.9); opacity: 0.7; }
        }

        /* Water Wave Animation */
        .water-wave {
          position: relative;
          overflow: hidden;
        }
        .water-wave::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: var(--wave-height, 50%);
          background: linear-gradient(180deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.4) 100%);
          border-radius: 0 0 8px 8px;
          z-index: 0;
        }
        .water-wave-content {
          position: relative;
          z-index: 1;
        }
      `}</style>
    </div>
  );
};

export default WaterIntakeCalculatorPage;